import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutlined,
  CloseOutlined,
  CloudUploadOutlined,
  FileTextOutlined,
} from '@ant-design/icons';

import useAuthenticatedRequest from '../../hooks/useAuthenticatedRequest';
import useDemoMode from '../../hooks/useDemoMode';

const PREVIEW_ROWS = 15;
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;
// If the status payload doesn't change for this many consecutive polls,
// the Celery chain has almost certainly finished — even if the counts
// don't satisfy the "stagingDone && journalDone" success condition (e.g.
// because process_staging_trades is deprecated and stuck at 0). Bail out
// of the poll loop and surface whatever progress we did capture.
const POLL_STABILITY_THRESHOLD = 4;

const STAGE_LABELS = [
  { key: 'process_staged_trades', label: 'Process staged trades' },
  { key: 'normalize_trades', label: 'Normalize trades' },
  { key: 'create_journal_entries', label: 'Create journal entries' },
  { key: 'refresh_performance_caggs', label: 'Refresh analytics' },
];

const WIZARD_STEPS = ['Choose file', 'Preview', 'Importing', 'Done'];

// Lightweight CSV preview parser. We don't ship Papa Parse just for a peek —
// IBKR and most broker exports use plain comma-separated rows without embedded
// commas in the columns we care about.
const parseCsvPreview = (text, maxRows = PREVIEW_ROWS) => {
  if (!text) return { headers: [], rows: [] };
  const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (!lines.length) return { headers: [], rows: [] };
  const splitLine = (line) => line.split(',').map((cell) => cell.trim());
  const headers = splitLine(lines[0]);
  const rows = lines.slice(1, 1 + maxRows).map((line) => splitLine(line));
  return { headers, rows, total: lines.length - 1 };
};

const extractErrorMessage = (err, fallback = 'Failed to process the CSV. Please try again.') => {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  const responseMsg = err.response?.data?.error || err.response?.data?.detail || err.response?.data?.message;
  if (responseMsg) return String(responseMsg);
  if (err.message) return err.message;
  return fallback;
};

// Pull HTTP status / target URL off whatever shape the error has. axios
// errors carry it on .response/.config; useAuthenticatedRequest re-throws
// them as plain Error('Request failed: <status> - <body>'), so we have to
// regex the message in that fallback case.
const extractErrorMeta = (err, attemptedUrl) => {
  if (!err) return { status: null, url: attemptedUrl };
  let status = err.response?.status ?? null;
  let url = err.response?.config?.url || err.config?.url || attemptedUrl;
  if (status === null && typeof err.message === 'string') {
    const m = err.message.match(/Request failed:\s*(\d{3})/);
    if (m) status = Number(m[1]);
  }
  return { status, url };
};

const stageStatus = (stageKey, progress) => {
  if (!progress) return 'pending';
  const { staging } = progress;
  const stagingDone = staging?.total > 0 && staging.processed >= staging.total;
  if (stageKey === 'process_staged_trades') {
    if (!staging?.total) return 'pending';
    if (staging.processed >= staging.total) return 'done';
    return 'active';
  }
  if (stageKey === 'normalize_trades') {
    if (!stagingDone) return 'pending';
    return progress.journal?.entries_created > 0 ? 'done' : 'active';
  }
  if (stageKey === 'create_journal_entries') {
    if (!stagingDone) return 'pending';
    return progress.journal?.entries_created > 0 ? 'done' : 'active';
  }
  if (stageKey === 'refresh_performance_caggs') {
    if (!stagingDone) return 'pending';
    return progress.journal?.entries_created > 0 ? 'done' : 'active';
  }
  return 'pending';
};

export default function ImportBrokerCSVFlyin({ open, onClose, onSuccess }) {
  const navigate = useNavigate();
  const { makeAuthenticatedRequest } = useAuthenticatedRequest();
  const { demoMode } = useDemoMode();

  const [step, setStep] = useState('choose'); // choose | preview | uploading | progress | done | error
  const [broker, setBroker] = useState('IBKR');
  const [file, setFile] = useState(null);
  const [previewState, setPreviewState] = useState({ headers: [], rows: [], total: 0 });
  const [error, setError] = useState('');
  const [errorMeta, setErrorMeta] = useState({ status: null, url: null });
  const [importVersion, setImportVersion] = useState(null);
  const [progress, setProgress] = useState(null);
  const [pollExpired, setPollExpired] = useState(false);

  const containerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const pollDeadlineRef = useRef(null);
  const lastFingerprintRef = useRef(null);
  const stableCountRef = useRef(0);

  const isComingSoon = broker === 'Tradestation';

  const resetWizard = useCallback(() => {
    setStep('choose');
    setBroker('IBKR');
    setFile(null);
    setPreviewState({ headers: [], rows: [], total: 0 });
    setError('');
    setErrorMeta({ status: null, url: null });
    setImportVersion(null);
    setProgress(null);
    setPollExpired(false);
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollDeadlineRef.current = null;
    successFiredRef.current = false;
    lastFingerprintRef.current = null;
    stableCountRef.current = 0;
  }, []);

  useEffect(() => {
    if (!open) resetWizard();
  }, [open, resetWizard]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => containerRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  useEffect(() => () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
  }, []);

  const handleFileChange = (event) => {
    setError('');
    const selected = event.target.files?.[0] ?? null;
    if (!selected) {
      setFile(null);
      setPreviewState({ headers: [], rows: [], total: 0 });
      return;
    }
    if (!selected.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a .csv file.');
      setFile(null);
      setPreviewState({ headers: [], rows: [], total: 0 });
      return;
    }
    setFile(selected);
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      setPreviewState(parseCsvPreview(text));
    };
    reader.onerror = () => {
      setError('Could not read the file for preview. You can still try uploading.');
      setPreviewState({ headers: [], rows: [], total: 0 });
    };
    reader.readAsText(selected);
  };

  const handleNextFromChoose = () => {
    if (!file) {
      setError('Choose a CSV file first.');
      return;
    }
    if (demoMode) {
      setError('Demo mode is on — turn it off in Settings before importing real trades.');
      return;
    }
    setError('');
    setStep('preview');
  };

  const successFiredRef = useRef(false);

  const broadcastImportComplete = useCallback((version) => {
    if (typeof window === 'undefined') return;
    try {
      window.dispatchEvent(
        new CustomEvent('tradapp:import-complete', { detail: { import_version: version } })
      );
    } catch (err) {
      if (import.meta.env?.DEV) console.warn('Failed to broadcast import-complete', err);
    }
  }, []);

  const finishImport = useCallback(
    (version, payload = null, expired = false) => {
      if (successFiredRef.current) return;
      successFiredRef.current = true;
      setStep('done');
      if (expired) setPollExpired(true);
      broadcastImportComplete(version);
      if (typeof onSuccess === 'function') {
        try {
          onSuccess({ import_version: version, expired, ...payload });
        } catch (err) {
          if (import.meta.env?.DEV) console.warn('onSuccess handler threw', err);
        }
      }
    },
    [broadcastImportComplete, onSuccess]
  );

  const pollStatus = useCallback(async (version) => {
    try {
      const data = await makeAuthenticatedRequest(`/api/v1/journal/imports/${version}/status/`, {
        method: 'GET',
        skipGlobalErrorHandler: true,
      });
      setProgress(data || null);

      const fingerprint = JSON.stringify({
        staging: data?.staging,
        journal: data?.journal,
        hint: data?.hint,
      });
      if (fingerprint === lastFingerprintRef.current) {
        stableCountRef.current += 1;
      } else {
        lastFingerprintRef.current = fingerprint;
        stableCountRef.current = 1;
      }

      const stagingTotal = data?.staging?.total || 0;
      const stagingProcessed = data?.staging?.processed || 0;
      const journalCreated = data?.journal?.entries_created || 0;
      const stagingDone = stagingTotal > 0 && stagingProcessed >= stagingTotal;
      const journalDone = journalCreated > 0;
      const isStable = stableCountRef.current >= POLL_STABILITY_THRESHOLD;

      if (stagingDone && journalDone) {
        finishImport(version, { progress: data });
        return;
      }
      // Celery chain runs in <1s for clean batches; if the status payload
      // doesn't move for ~8s (4 polls × 2s) we assume the chain is done
      // and surface whatever counts we have, with a warning if they're
      // incomplete. Avoids hanging the wizard for 5 minutes when the
      // pipeline silently no-ops (e.g. deprecated processor).
      if (isStable) {
        const incomplete = !stagingDone || !journalDone;
        finishImport(version, { progress: data }, incomplete);
        return;
      }
      if (Date.now() > (pollDeadlineRef.current || 0)) {
        finishImport(version, { progress: data }, true);
        return;
      }
      pollTimerRef.current = setTimeout(() => pollStatus(version), POLL_INTERVAL_MS);
    } catch (err) {
      // Don't abort the wizard on a transient status fetch error — keep trying.
      if (import.meta.env?.DEV) console.warn('Import status poll failed', err);
      pollTimerRef.current = setTimeout(() => pollStatus(version), POLL_INTERVAL_MS);
    }
  }, [makeAuthenticatedRequest, finishImport]);

  const handleConfirmUpload = async () => {
    if (!file) return;
    setError('');
    setErrorMeta({ status: null, url: null });
    successFiredRef.current = false;
    setStep('uploading');
    const uploadUrl = '/api/v1/ibkr/trades/upload/';
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await makeAuthenticatedRequest(uploadUrl, {
        method: 'POST',
        data: formData,
        skipGlobalErrorHandler: true,
      });
      const version = response?.import_version || response?.importVersion || null;
      setImportVersion(version);
      if (version) {
        pollDeadlineRef.current = Date.now() + POLL_TIMEOUT_MS;
        setStep('progress');
        pollStatus(version);
      } else {
        // No version returned — surface the done page with whatever the
        // backend handed back, and notify the parent so it can dismiss
        // the import card.
        finishImport(null, { response });
      }
    } catch (err) {
      setError(extractErrorMessage(err));
      setErrorMeta(extractErrorMeta(err, uploadUrl));
      setStep('error');
    }
  };

  const handleGoToJournal = () => {
    onClose?.();
    navigate('/journal');
  };

  const handleViewOpenPositions = () => {
    onClose?.();
    navigate('/dashboard');
  };

  const stagingTotal = progress?.staging?.total ?? 0;
  const stagingProcessed = progress?.staging?.processed ?? 0;
  const stagingPct = stagingTotal > 0 ? Math.round((stagingProcessed / stagingTotal) * 100) : 0;
  const journalCreated = progress?.journal?.entries_created ?? 0;

  const activeStepIndex = useMemo(() => {
    switch (step) {
      case 'choose':
        return 0;
      case 'preview':
        return 1;
      case 'uploading':
      case 'progress':
        return 2;
      case 'done':
        return 3;
      case 'error':
        // Error always surfaces during/right after the Importing step — keep
        // it pinned there so the Stepper's red icon lands on step 2.
        return 2;
      default:
        return 0;
    }
  }, [step]);

  // When the wizard reaches `done` but the result is a failure (zero rows
  // processed, zero journal entries, or the poll bailed out before the chain
  // signalled success), the Stepper should NOT show all-green checkmarks.
  // Compute a per-step completed/error map so renderDone()'s warning banners
  // are mirrored in the step indicator.
  const stepStates = useMemo(() => {
    const states = [
      { completed: false, error: false },
      { completed: false, error: false },
      { completed: false, error: false },
      { completed: false, error: false },
    ];
    // Default MUI behaviour: every step before activeStep is completed.
    for (let i = 0; i < activeStepIndex; i += 1) states[i].completed = true;

    if (step === 'error') {
      // Upload itself failed: Choose + Preview done, Importing errored.
      states[0].completed = true;
      states[1].completed = true;
      states[2] = { completed: false, error: true };
      return states;
    }

    if (step === 'done') {
      const stagingComplete = stagingTotal > 0 && stagingProcessed >= stagingTotal;
      const noJournalEntries = journalCreated === 0;
      const fullSuccess = stagingComplete && !noJournalEntries;
      if (!fullSuccess) {
        // The Celery chain finished but didn't produce trade history.
        // Mark Importing as errored (that's where the data was lost) and
        // leave Done un-completed so the user sees something is off.
        states[2] = { completed: false, error: true };
        states[3] = { completed: false, error: false };
      }
    }
    return states;
  }, [activeStepIndex, step, stagingTotal, stagingProcessed, journalCreated]);

  const renderChoose = () => (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Pick your broker and the CSV export. We&apos;ll show you a preview before anything touches your account.
      </Typography>
      <FormControl fullWidth>
        <InputLabel id="broker-label">Broker</InputLabel>
        <Select
          labelId="broker-label"
          label="Broker"
          value={broker}
          onChange={(e) => setBroker(e.target.value)}
          MenuProps={{ disablePortal: true, container: () => containerRef.current, keepMounted: true }}
        >
          <MenuItem value="IBKR">Interactive Brokers (IBKR)</MenuItem>
          <MenuItem value="Tradestation">TradeStation</MenuItem>
        </Select>
      </FormControl>
      {isComingSoon ? <Alert severity="info">TradeStation support is coming soon.</Alert> : null}
      <Button
        variant="outlined"
        component="label"
        startIcon={<FileTextOutlined />}
        disabled={isComingSoon}
        sx={{ alignSelf: 'flex-start' }}
      >
        {file ? 'Choose a different file' : 'Choose CSV file'}
        <input type="file" hidden accept=".csv" onChange={handleFileChange} />
      </Button>
      {file ? (
        <Typography variant="body2" color="text.primary">
          <strong>{file.name}</strong> · {(file.size / 1024).toFixed(1)} KB
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">
          Accepted format: .csv (exported from your broker)
        </Typography>
      )}
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleNextFromChoose}
          disabled={!file || isComingSoon}
        >
          Next: preview
        </Button>
      </Stack>
    </Stack>
  );

  const renderPreview = () => (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        First {previewState.rows.length} of ~{previewState.total} rows. We&apos;ll process the whole file once
        you confirm.
      </Typography>
      <Paper variant="outlined">
        <TableContainer sx={{ maxHeight: 360 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {previewState.headers.map((h, idx) => (
                  <TableCell key={`${h}-${idx}`} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {previewState.rows.length ? previewState.rows.map((row, ridx) => (
                <TableRow key={ridx}>
                  {row.map((cell, cidx) => (
                    <TableCell key={cidx} sx={{ whiteSpace: 'nowrap' }}>{cell}</TableCell>
                  ))}
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={previewState.headers.length || 1} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    Couldn&apos;t parse a preview. Confirm to send the file as-is.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={() => setStep('choose')}>Back</Button>
        <Button variant="contained" onClick={handleConfirmUpload} startIcon={<CloudUploadOutlined />}>
          Confirm & import
        </Button>
      </Stack>
    </Stack>
  );

  const renderUploading = () => (
    <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">Uploading the file and registering the import…</Typography>
    </Stack>
  );

  const renderProgress = () => (
    <Stack spacing={3}>
      <Box>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Import batch <strong>#{importVersion}</strong> · {stagingProcessed} of {stagingTotal} rows processed
        </Typography>
        <LinearProgress variant="determinate" value={stagingPct} sx={{ height: 8, borderRadius: 4 }} />
      </Box>

      <Stack spacing={1.5}>
        {STAGE_LABELS.map((stage) => {
          const status = stageStatus(stage.key, progress);
          return (
            <Stack key={stage.key} direction="row" spacing={1.5} alignItems="center">
              {status === 'done' ? (
                <CheckCircleOutlined style={{ color: '#09B83A', fontSize: 18 }} />
              ) : status === 'active' ? (
                <CircularProgress size={16} thickness={5} />
              ) : (
                <Box sx={{ width: 16, height: 16, borderRadius: '50%', border: 1, borderColor: 'divider' }} />
              )}
              <Typography variant="body2" sx={{ color: status === 'done' ? 'text.primary' : 'text.secondary' }}>
                {stage.label}
              </Typography>
            </Stack>
          );
        })}
      </Stack>

      <Divider />
      <Stack spacing={1}>
        <Typography variant="caption" color="text.secondary">Live counts</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip size="small" label={`Staged: ${stagingTotal}`} variant="outlined" />
          <Chip size="small" label={`Processed: ${stagingProcessed}`} variant="outlined" />
          <Chip size="small" label={`Journal entries: ${journalCreated}`} color={journalCreated > 0 ? 'success' : 'default'} variant="outlined" />
        </Stack>
        {progress?.hint ? <Alert severity="info">{progress.hint}</Alert> : null}
      </Stack>
    </Stack>
  );

  const renderDone = () => {
    const stagingComplete = stagingTotal > 0 && stagingProcessed >= stagingTotal;
    const noJournalEntries = journalCreated === 0;
    const partial = pollExpired && (!stagingComplete || noJournalEntries);
    const fullSuccess = stagingComplete && !noJournalEntries;

    let banner;
    if (fullSuccess) {
      banner = (
        <Alert
          severity="success"
          icon={<CheckCircleOutlined style={{ color: '#09B83A' }} />}
        >
          Import complete. Trade history is in your account.
        </Alert>
      );
    } else if (partial && !stagingComplete) {
      banner = (
        <Alert severity="warning">
          The Celery chain finished but only <strong>{stagingProcessed}</strong> of <strong>{stagingTotal}</strong> staged rows
          were processed. Likely the backend processor for this broker is deprecated or the staged rows
          didn&apos;t match the new schema — check the worker logs for hints.
        </Alert>
      );
    } else if (partial && noJournalEntries) {
      banner = (
        <Alert severity="warning">
          Rows were staged and processed, but no journal entries were created. This usually means the
          batch had no SELL executions to pair with the BUYs (or vice versa). Open trades will still show
          up once you add the closing legs.
        </Alert>
      );
    } else {
      banner = (
        <Alert severity="info">
          Background processing is still running. You can leave this open or close it — the dashboards
          will refresh when the worker finishes.
        </Alert>
      );
    }

    return (
      <Stack spacing={3}>
        {banner}

        <Box>
          <Typography variant="overline" color="text.secondary">Result</Typography>
          <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Rows imported</Typography>
              <Typography variant="h5">{stagingTotal}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Rows processed</Typography>
              <Typography variant="h5" sx={{ color: stagingComplete ? 'inherit' : 'warning.main' }}>
                {stagingProcessed}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Journal entries created</Typography>
              <Typography variant="h5" sx={{ color: noJournalEntries ? 'warning.main' : 'inherit' }}>
                {journalCreated}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {progress?.hint ? <Alert severity="info">{progress.hint}</Alert> : null}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={onClose}>Close</Button>
          <Button variant="outlined" onClick={handleViewOpenPositions}>
            View Open Positions
          </Button>
          <Button variant="contained" onClick={handleGoToJournal} disabled={noJournalEntries}>
            Go to Journal
          </Button>
        </Stack>
      </Stack>
    );
  };

  const renderError = () => {
    const { status, url } = errorMeta;
    let hint = null;
    if (status === 404) {
      hint = (
        <Alert severity="info">
          The backend route was not found. Verify that the Django server is running and that the IBKR
          upload endpoint is registered. You can check from a terminal:
          <Box component="pre" sx={{ mt: 1, p: 1, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 1, fontSize: '0.75rem' }}>
{`curl -i -X POST <backend-host>${url || '/api/v1/ibkr/trades/upload/'} -H "Authorization: Bearer <token>"`}
          </Box>
        </Alert>
      );
    } else if (status === 401 || status === 403) {
      hint = (
        <Alert severity="info">
          The request was rejected by Clerk auth. Sign out and back in, then retry.
        </Alert>
      );
    } else if (status === 413) {
      hint = (
        <Alert severity="info">
          The file is larger than the backend accepts. Split the CSV or raise the server upload limit.
        </Alert>
      );
    } else if (status >= 500) {
      hint = (
        <Alert severity="info">
          The backend hit a server error. Check the Django logs for the matching request id.
        </Alert>
      );
    }

    return (
      <Stack spacing={2}>
        <Alert severity="error">
          {error || 'Something went wrong during the import.'}
          {status || url ? (
            <Box sx={{ mt: 1, fontSize: '0.75rem', opacity: 0.85 }}>
              {status ? <Box>HTTP {status}</Box> : null}
              {url ? <Box>Endpoint: <strong>{url}</strong></Box> : null}
            </Box>
          ) : null}
        </Alert>
        {hint}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={onClose}>Close</Button>
          <Button variant="contained" onClick={() => setStep('preview')}>Try again</Button>
        </Stack>
      </Stack>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      data-testid="flyin-import-broker"
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: '90vw', md: '60vw', lg: '50vw' },
          minWidth: { md: 760 },
          maxWidth: '100vw',
          bgcolor: 'background.paper',
          color: 'text.primary',
          backgroundImage: 'none',
          outline: 'none',
        },
      }}
    >
      <Box ref={containerRef} tabIndex={-1} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, height: '100%', overflowY: 'auto' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6">Import Broker CSV</Typography>
            <Typography variant="body2" color="text.secondary">
              Bring your trade history into Tradatomy. We&apos;ll guide you through the steps.
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close import wizard"><CloseOutlined /></IconButton>
        </Stack>

        <Stepper activeStep={activeStepIndex} alternativeLabel>
          {WIZARD_STEPS.map((label, idx) => {
            const { completed, error: hasError } = stepStates[idx] || {};
            return (
              <Step key={label} completed={completed}>
                <StepLabel error={hasError}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>

        <Divider />

        {step === 'choose' ? renderChoose() : null}
        {step === 'preview' ? renderPreview() : null}
        {step === 'uploading' ? renderUploading() : null}
        {step === 'progress' ? renderProgress() : null}
        {step === 'done' ? renderDone() : null}
        {step === 'error' ? renderError() : null}
      </Box>
    </Drawer>
  );
}

ImportBrokerCSVFlyin.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
};

ImportBrokerCSVFlyin.defaultProps = {
  open: false,
  onClose: () => {},
  onSuccess: () => {},
};
